import {deepMergeWithValid, omit} from '@shared/helpers/object'

import {getResultInfo, productItemsMapper} from '@web/actions/result'
import {PayMethod} from '@web/core/constants'
import {decodeHtmlEntities as decode} from '@web/helpers/htmlEntities'
import {getDeliveryTag} from '@web/helpers/policies'
import {ResultProduct} from '@web/slices/result/slice'
import makeSlice from '@web/slices/result/sliceMaker'

// 20230422
type RawDateString = string

// 0600
type RawTimeString = string

function formatDate(date: RawDateString) {
    return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
}

function formatTime(date: Maybe<RawTimeString>) {
    return date ? `${date.slice(0, 2)}:${date.slice(2, 4)}` : date
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder // 주문결과 조회
            .addCase(
                getResultInfo.fulfilled,
                (
                    state,
                    {payload: {delivery, order, pay, products, toastPopup, deliveryAddressBook, ...restPayload}},
                ) => {
                    state.orderKey = restPayload.orderKey
                    state.order = {
                        orderNo: order.orderNo,
                        orderSheetId: order.cartSnapshotSeq,
                        dawnArrivalIncluded: order.dawnArrivalIncluded,
                        deliveryMemoParticularInput: order.deliveryMemoParticularInput ?? false,
                        orderOptionInfoParticularInput: order.orderOptionInfoParticularInput ?? false,
                        preOrderIncluded: order.preOrderIncluded,
                        todayDispatchIncluded: order.todayDispatchIncluded,
                        arrivalGuaranteeIncluded: order.arrivalGuaranteeIncluded,
                        deliveryChangeable: deliveryAddressBook?.deliveryAddressChangeable ?? false,
                    }
                    state.pay = deepMergeWithValid(
                        {...omit('promotionApplyAmount', pay), isCardPromotionUsed: !!pay.promotionApplyAmount},
                        state.pay,
                    )

                    /**
                     * 삼성페이의 경우 실제로는 신용카드와 동일하게 동작
                     * pay.payAdmission은 CARD 로 내려오되, pay.payAdmisstion.payAuthenticationType (인증타입)이 SAMSUNG_PAY로 내려온다.
                     * @see https://oss.fin.navercorp.com/payOrder/order-qa/issues/4651#issuecomment-767475
                     */
                    if (pay.payAdmission?.payAuthenticationType === 'SAMSUNG_PAY') {
                        state.pay.payAdmission.payMethodType = PayMethod.SAMSUNG_PAY
                    } else if (pay.payAdmission?.payAuthenticationType === 'APPLE_PAY') {
                        state.pay.payAdmission.payMethodType = PayMethod.APPLE_PAY
                    }
                    state.totalDeliveryFeeAmount = restPayload.totalDeliveryFeeAmount
                    state.totalDiscountAmount = restPayload.totalDiscountAmount
                    state.totalOrderAmount = restPayload.totalOrderAmount
                    state.totalPurchaserCommissionAmount = products.reduce(
                        (acc, {purchaserCommissionAmount}) => acc + (purchaserCommissionAmount ?? 0),
                        0,
                    )
                    state.products = products.map<ResultProduct>((product) => {
                        return {
                            additionalInfos: product.additionalInfos,
                            ...(product.branchName && product.branchDelivery
                                ? {
                                      branch: {
                                          name: product.branchName,
                                          delivery: {
                                              date: formatDate(product.branchDelivery.branchDeliveryDate),
                                              endTime: formatTime(product.branchDelivery.branchDeliveryEndTime) ?? '',
                                              preparationMinutes:
                                                  product.branchDelivery.branchDeliveryPreparationMinutes ?? 0,
                                              durationMinutes:
                                                  product.branchDelivery.branchDeliveryDurationMinutes ?? 0,
                                              slotAvailable: product.branchDelivery.useBranchDeliverySlot,
                                              slotId: product.branchDelivery.branchDeliverySlotId,
                                              startTime:
                                                  formatTime(product.branchDelivery.branchDeliveryStartTime) ?? '',
                                          },
                                      },
                                  }
                                : null),
                            branchBenefit: product.branchBenefitValue
                                ? {
                                      applyValue: product.branchBenefitApplyValue,
                                      value: product.branchBenefitValue,
                                  }
                                : null,
                            channelNo: product.channelNo ?? undefined,
                            customOrderDetail: product.customOrderDetailType,
                            ...(product.deliveryAttributeType
                                ? {
                                      deliveryAttributeType: product.deliveryAttributeType,
                                  }
                                : null),
                            deliveryGroupKey: product.deliveryGroupKey,
                            deliveryMemo: decode(product.deliveryMemo ?? ''),
                            templateMemo: product.templateMemo ?? false,
                            deliveryMethodType: product.deliveryMethodType,
                            deliveryPolicyApplied: product.deliveryPolicyApplied,
                            // 배송시뮬레이션 정보 중 최적 정보만 저장
                            deliveryPrediction: product.deliveryPrediction && {
                                ...product.deliveryPrediction,
                                best: true,
                                visible: true,
                            },
                            dispatchDueDateTime: product.dispatchDueDateTime ?? '',
                            ecNvMid: product.ecNvMid ?? null,
                            ecMallPid: product.ecMallPid ?? undefined,
                            clickLogId: product.clickLogId ?? undefined,
                            accountId: product.accountId ?? undefined,
                            ecouponBarcodeConnected: product.ecouponBarcodeConnect,
                            gift: product.gift ?? '',
                            /** 가맹점 주문완료는 희망배송일 정보 미제공 */
                            hopeDelivery: product.hopeDelivery
                                ? {
                                      additionalFee: product.hopeDelivery.additionalFee,
                                      date: product.hopeDelivery.hopeDeliveryDate,
                                      region: product.hopeDelivery.region,
                                      time: product.hopeDelivery.hopeDeliveryTime,
                                  }
                                : null,
                            isEcoupon: product.ecoupon,
                            items: productItemsMapper(product.items),
                            merchantAccountId: product.merchantAccountId ?? '',
                            merchantId: product.merchantId ?? '',
                            merchantNo: product.merchantNo,
                            naverBenefit: product.naverBenefit,
                            orderDateTime: product.orderDateTime ?? '',
                            orderQuantity: product.orderQuantity,
                            initOrderQuantity: product.initOrderQuantity ?? undefined,
                            overseasDelivery: product.overseasDelivery,
                            preOrder: product.preOrder ?? undefined,
                            largeCategoryId: product.largeCategoryId ?? null,
                            middleCategoryId: product.middleCategoryId ?? null,
                            smallCategoryId: product.smallCategoryId ?? null,
                            detailedCategoryId: product.detailedCategoryId ?? null,
                            productCategoryId: product.productCategoryId ?? null,
                            productImageUrl: product.productImageUrl ?? '',
                            productName: product.productName,
                            originalProductNo: product.originalProductNo ?? '',
                            productNo: product.productNo,
                            productOrderNo: product.productOrderNo,
                            productOrderStatus: product.productOrderStatus ?? '',
                            productPaymentAmount: product.productPaymentAmount ?? 0,
                            initProductPayAmount: product.initProductPayAmount ?? undefined,
                            productUrl: product.productUrl ?? restPayload.defaultProductUrl,
                            purchaserCommissionAmount: product.purchaserCommissionAmount,
                            serviceType: product.serviceType,
                            totalOrderAmount: product.totalOrderAmount,
                            totalPayAmount: product.totalPayAmount,
                            merchantChannelName: product.merchantChannelName,
                            merchantChannelServiceType: product.merchantChannelServiceType,
                            deliveryAttributeType: product.deliveryAttributeType,
                            deliveryTagType: getDeliveryTag({
                                arrivalDate: product.arrivalGuarantee?.arrivalGuaranteeDateTime,
                                deliveryAttributeType: product.deliveryAttributeType ?? undefined,
                                deliveryTagType: product.deliveryTagType,
                                orderDate: product.orderDateTime,
                            }),
                            arrivalGuarantee: product.arrivalGuarantee,
                            todayDispatch: product.todayDispatch,
                            eCouponBarcodeNos: product.eCouponBarcodeNos,
                            installationProduct: product.installationProduct,
                        }
                    })

                    if (deliveryAddressBook) {
                        state.deliveryAddressBook = {
                            baseAddress: deliveryAddressBook.baseAddress,
                            detailAddress: deliveryAddressBook.detailAddress,
                            receiverName: deliveryAddressBook.receiverName,
                            telNo1: deliveryAddressBook.telNo1,
                            telNo2: deliveryAddressBook.telNo2,
                            zipCode: deliveryAddressBook.zipCode,
                            ...(deliveryAddressBook.pickupLocationType
                                ? {locationType: deliveryAddressBook.pickupLocationType}
                                : {}),
                            ...(deliveryAddressBook.pickupLocationContent
                                ? {locationDescription: decode(deliveryAddressBook.pickupLocationContent)}
                                : {}),
                            ...(deliveryAddressBook.entryMethodType
                                ? {entryMethodType: deliveryAddressBook.entryMethodType}
                                : {}),
                            ...(deliveryAddressBook.entryMethodContent
                                ? {entryDescription: deliveryAddressBook.entryMethodContent}
                                : {}),
                            deliveryAddressHash: deliveryAddressBook.deliveryAddressHash,
                            virtualPhoneNumber: deliveryAddressBook.virtualPhoneNumber,
                        }
                    }

                    state.delivery = delivery
                    state.deliveryNothingOrder = restPayload.deliveryNothingOrder
                    state.toastPopup = {
                        ...toastPopup,
                        expectTotalRewardPointOfTodayOrders: toastPopup.expectTotalRewardPointOfTodayOrders ?? 0,
                    }
                    state.pendingPayment = restPayload.pendingPayment
                    state.merchantsByMerchantNo = restPayload.merchantsByMerchantNo
                    state.deposit = restPayload.deposit
                    state.purchaser.naverId = restPayload.purchaser.naverId
                    state.purchaser.naverIdNo = restPayload.purchaser.naverIdNo
                    state.purchaser.chargePointUserStatusType = restPayload.purchaser.chargePointUserStatusType

                    if (restPayload.familyPayOrder) {
                        state.familyPayOrder = {
                            payerPayMemberNo: 0,
                            requestMemo: decode(restPayload.familyPayOrder.familyPayRequestMemo),
                            organizerName: '',
                            /** familyPayPaymentType이 null인 경우는 패밀리결제 2차스펙 이전 주문건으로 승인결제이다. */
                            paymentType: restPayload.familyPayOrder.familyPayPaymentType ?? 'APPROVAL_PAYMENT',
                        }
                    }
                    state.exposeFamilyPayBanner.organizer = restPayload.exposeFamilyPayBannerForOrganizer ?? false
                    state.exposeFamilyPayBanner.member = restPayload.exposeFamilyPayBannerForMember ?? false

                    state.exposeFamilyPayBanner.member = restPayload.exposeFamilyPayBannerForMember ?? false

                    /** 선물하기 수신자 정보 */
                    state.gifting.receiverCount = restPayload.gifting?.receiverCount ?? 0
                    state.gifting.productOrderGiftingInfos =
                        restPayload.gifting?.productOrderGiftingInfos?.map((receiver) => ({
                            productOrderNo: receiver.productOrderNo,
                            name: receiver.receiverName,
                            idNo: receiver.receiverIdNo,
                            senderNickName: receiver.senderNickName,
                            keyType: receiver.giftingReceiverKeyType,
                            dueDateTime: receiver.giftReceivingDueDateTime,
                            giftingBoxShortUrl: receiver.giftingBoxShortUrl,
                        })) ?? []

                    state.talkChannelId =
                        products.find(({fleaMarket}) => !!fleaMarket?.talkChannelId)?.fleaMarket?.talkChannelId ?? ''
                },
            )
    },
})

export default slice.reducer
